import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Dog, Stethoscope, Users, MessagesSquare, PawPrint, GraduationCap, BookOpen, Settings, Heart, Crown, LogOut, Store, Menu, X,
} from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/dogs", label: "My Dogs", icon: Dog, testid: "nav-dogs" },
  { to: "/research", label: "AI Vet Research", icon: Stethoscope, testid: "nav-research", premium: true },
  { to: "/behaviour", label: "Pack & Behaviour", icon: Users, testid: "nav-behaviour", premium: true },
  { to: "/training", label: "Training", icon: GraduationCap, testid: "nav-training" },
  { to: "/courses", label: "Courses", icon: BookOpen, testid: "nav-courses" },
  { to: "/directory", label: "Directory", icon: Store, testid: "nav-directory" },
  { to: "/community", label: "Community", icon: MessagesSquare, testid: "nav-community" },
  { to: "/about", label: "My Story", icon: Heart, testid: "nav-about" },
  { to: "/manage", label: "Manage", icon: Settings, testid: "nav-manage", adminOnly: true },
];

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const visibleNav = nav.filter((n) => !n.adminOnly || user?.is_admin);
  const mobileNav = visibleNav.slice(0, 5);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-border bg-card/60 backdrop-blur px-4 py-6 sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-2 mb-8">
          <div className="h-10 w-10 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground"><PawPrint className="h-5 w-5" /></div>
          <div>
            <div className="font-head font-extrabold text-lg leading-none tracking-tight">My Pack</div>
            <div className="text-xs text-muted-foreground">Health, behaviour & training</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1 overflow-y-auto scroll-thin">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
                className={({ isActive }) => `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                <Icon className="h-[18px] w-[18px]" />
                <span className="flex-1">{n.label}</span>
                {n.premium && !user?.is_premium && <Crown className="h-3.5 w-3.5 text-secondary" />}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto pt-4">
          {!user?.is_premium && (
            <NavLink to="/upgrade" data-testid="nav-upgrade" className="block rounded-2xl bg-gradient-to-br from-secondary/15 to-accent/10 border border-secondary/30 p-4 mb-3 hover:-translate-y-0.5 transition-transform">
              <div className="flex items-center gap-2 text-secondary font-head font-semibold text-sm"><Crown className="h-4 w-4" /> Go Premium</div>
              <p className="text-xs text-muted-foreground mt-1">Unlock AI research, pack insights & courses.</p>
            </NavLink>
          )}
          <div className="flex items-center gap-2 rounded-xl border border-border p-2.5">
            <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-1">{user?.is_premium ? <><Crown className="h-3 w-3 text-secondary" /> Premium</> : "Free plan"}</div>
            </div>
            <button data-testid="logout-btn" onClick={doLogout} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden sticky top-0 z-20 flex items-center justify-between px-4 h-14 border-b border-border bg-card/80 backdrop-blur">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-primary flex items-center justify-center text-primary-foreground"><PawPrint className="h-4 w-4" /></div>
            <span className="font-head font-extrabold">My Pack</span>
          </div>
          <button data-testid="mobile-menu-btn" onClick={() => setMenuOpen(true)} className="p-2 text-muted-foreground"><Menu className="h-5 w-5" /></button>
        </header>

        <AnimatePresence>
          {menuOpen && (
            <motion.div className="md:hidden fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
              <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.25 }}
                className="absolute right-0 top-0 h-full w-72 bg-card border-l border-border p-5 flex flex-col overflow-y-auto scroll-thin">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-head font-extrabold text-lg">Menu</span>
                  <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-border p-2.5 mb-4">
                  <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold overflow-hidden">{user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : initials}</div>
                  <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{user?.name || user?.email}</div><div className="text-[11px] text-muted-foreground flex items-center gap-1">{user?.is_premium ? <><Crown className="h-3 w-3 text-secondary" /> Premium</> : "Free plan"}</div></div>
                </div>
                <nav className="flex flex-col gap-1">
                  {visibleNav.map((n) => {
                    const Icon = n.icon;
                    return (
                      <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`m-${n.testid}`} onClick={() => setMenuOpen(false)}
                        className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                        <Icon className="h-[18px] w-[18px]" />
                        <span className="flex-1">{n.label}</span>
                        {n.premium && !user?.is_premium && <Crown className="h-3.5 w-3.5 text-secondary" />}
                      </NavLink>
                    );
                  })}
                  {!user?.is_premium && (
                    <NavLink to="/upgrade" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-secondary hover:bg-secondary/10"><Crown className="h-[18px] w-[18px]" /> Go Premium</NavLink>
                  )}
                </nav>
                <button data-testid="logout-btn-mobile" onClick={() => { setMenuOpen(false); doLogout(); }} className="mt-auto flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted"><LogOut className="h-4 w-4" /> Log out</button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.main key={location.pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex-1 p-5 sm:p-8 max-w-[1400px] w-full mx-auto">
          {children}
        </motion.main>
        <nav className="md:hidden sticky bottom-0 z-20 grid grid-cols-5 border-t border-border bg-card/90 backdrop-blur">
          {mobileNav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[9px] ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                <Icon className="h-5 w-5" />
                {n.label.split(" ")[0]}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
